import React from 'react';
import { motion } from 'framer-motion';
import { X, ExternalLink, Info, CheckCircle } from 'lucide-react';
import { Product } from '../services/api';

interface ToolModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

const ToolModal: React.FC<ToolModalProps> = ({ product, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] rounded-lg">
              <span className="text-2xl">{product.icon}</span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {product.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {product.tagline}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status and Category */}
          <div className="flex items-center space-x-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              product.status === 'operational' 
                ? 'bg-green-100 text-green-800' 
                : product.status === 'beta'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
            </span>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {product.category}
            </span>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              About {product.name}
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {product.description}
            </p>
          </div>

          {/* Key Features */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Key Features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {product.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Metrics */}
          {Object.keys(product.metrics).length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Performance Metrics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(product.metrics).map(([key, value]) => (
                  <div key={key} className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {value}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Target Users */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Target Users
            </h3>
            <div className="flex flex-wrap gap-2">
              {product.users.map((user, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                >
                  {user}
                </span>
              ))}
            </div>
          </div>

          {/* Slack Channels */}
          {product.slackChannels && product.slackChannels.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Slack Channels
              </h3>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                <div className="flex flex-wrap gap-2">
                  {product.slackChannels.map((channel, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-2 bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 rounded-lg text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors text-sm font-medium"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52-2.523A2.528 2.528 0 0 1 5.042 10.12h2.52v2.522a2.528 2.528 0 0 1-2.52 2.523zm0-6.341H2.52A2.528 2.528 0 0 0 0 11.347a2.528 2.528 0 0 0 2.52 2.523h2.522v-2.523V8.824z"/>
                        <path d="M18.958 8.824a2.528 2.528 0 0 1 2.52 2.523A2.528 2.528 0 0 1 18.958 13.87h-2.52v-2.522a2.528 2.528 0 0 1 2.52-2.523zm0 6.341h2.522A2.528 2.528 0 0 0 24 12.642a2.528 2.528 0 0 0-2.52-2.523h-2.522v2.523v2.522z"/>
                        <path d="M8.824 5.042a2.528 2.528 0 0 1 2.523-2.52A2.528 2.528 0 0 1 13.87 5.042v2.52h-2.523A2.528 2.528 0 0 1 8.824 5.042zm6.341 0v-2.52A2.528 2.528 0 0 1 17.688 0a2.528 2.528 0 0 1 2.523 2.52v2.522h-2.523A2.528 2.528 0 0 1 15.165 5.042z"/>
                      </svg>
                      {channel}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Contact Person */}
          {product.contactPerson && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Contact Person
              </h3>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">
                        {product.contactPerson.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {product.contactPerson.name}
                        </h4>
                        <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                          {product.contactPerson.role}
                        </p>
                      </div>
                      <div>
                        <a 
                          href={`mailto:${product.contactPerson.email}`}
                          className="inline-flex items-center px-3 py-2 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-sm font-medium"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {product.contactPerson.email}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tool Access Information */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  Tool Access
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {product.url 
                    ? `Click "Open Tool" to access ${product.name} directly. The tool will open in a new tab.`
                    : `This tool is currently in development. The URL will be available soon. Please contact the development team for access.`
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => {
              if (product.url) {
                window.open(product.url, '_blank', 'noopener,noreferrer');
                onClose();
              } else {
                alert(`No tool URL available for ${product.name}. Please contact the development team.`);
              }
            }}
            className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all"
          >
            <ExternalLink className="w-4 h-4" />
            <span>Open Tool</span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ToolModal;
